import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { AccessibilityAnchor } from "@faire/retailer-visitor-shared/lib/AccessibilityLink";
import { shouldShowImprintPage } from "@faire/retailer-visitor-shared/lib/shouldShowImprintPage";
import { useSettingCookieConsentKey } from "@faire/retailer-visitor-shared/settings/useSettingCookieConsentKey";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotNull } from "@faire/web--source/common/typescriptUtils";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import copyrightPagePath from "@faire/web-api--source/routes/copyright";
import imprintPagePath from "@faire/web-api--source/routes/imprint";
import privacyPagePath from "@faire/web-api--source/routes/privacy";
import cookiesPagePath from "@faire/web-api--source/routes/privacy/cookies";
import siteMapPagePath from "@faire/web-api--source/routes/site-map";
import termsOfServicePagePath from "@faire/web-api--source/routes/tos";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
var LegalLinksBase = function(param) {
    var className = param.className, inversed = param.inversed, backgroundColor = param.backgroundColor;
    var cookieConsentKey = useSettingCookieConsentKey();
    var links = useLinks(!!cookieConsentKey, shouldShowImprintPage());
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        $backgroundColor: backgroundColor,
        $inversed: inversed,
        children: [
            /*#__PURE__*/ _jsxs(Typography, {
                color: Core.text.subdued,
                variant: "paragraphSansRegular",
                children: [
                    "\xa9",
                    new Date().getFullYear(),
                    " Faire Wholesale, Inc."
                ]
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsx(WrappingRow, {
                children: links.map(function(item, index) {
                    return /*#__PURE__*/ _jsxs(Row, {
                        children: [
                            /*#__PURE__*/ _jsx(AccessibilityAnchor, {
                                href: item.link,
                                target: "_blank",
                                rel: "noopener noreferrer",
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    color: Core.text.subdued,
                                    children: item.text
                                })
                            }),
                            index + 1 === links.length ? null : /*#__PURE__*/ _jsx(SeparatorTypography, {
                                children: " • "
                            })
                        ]
                    }, "".concat(item.text, "-").concat(index));
                })
            })
        ]
    });
};
export var LegalLinks = observer(LegalLinksBase);
var WrappingRow = styled(Row).withConfig({
    componentId: "sc-53d8f8a3-0"
})([
    "flex-wrap:wrap;"
]);
var Wrapper = styled(Column).withConfig({
    componentId: "sc-53d8f8a3-1"
})([
    "",
    " ",
    ""
], function(props) {
    var _props_$backgroundColor;
    return props.$backgroundColor ? css([
        "background-color:",
        ";transition:background-color 500ms linear;"
    ], (_props_$backgroundColor = props.$backgroundColor) !== null && _props_$backgroundColor !== void 0 ? _props_$backgroundColor : Core.surface.inverse) : "";
}, function(props) {
    return props.$inversed ? css([
        "p{color:",
        ";}"
    ], Core.text.inverse) : "";
});
var SeparatorTypography = styled(Typography).attrs({
    color: "graphite"
}).withConfig({
    componentId: "sc-53d8f8a3-2"
})([
    "&&&{padding:",
    " !important;}"
], getSpacing(0, "1x"));
var useLinks = function(includeCookies, includeImprint) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    return [
        {
            link: termsOfServicePagePath(),
            text: strictLocalize({
                id: "32rBNK",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Terms of Service"
                    }
                ]
            })
        },
        {
            link: privacyPagePath(),
            text: strictLocalize({
                id: "vx0nkZ",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Privacy Policy"
                    }
                ]
            })
        },
        includeCookies ? {
            link: cookiesPagePath(),
            text: strictLocalize({
                id: "cA0kQY",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Cookie Policy"
                    }
                ]
            })
        } : null,
        {
            link: copyrightPagePath(),
            text: strictLocalize({
                id: "1ofCOT",
                defaultMessage: [
                    {
                        type: 0,
                        value: "IP Policy"
                    }
                ]
            })
        },
        {
            link: siteMapPagePath(),
            text: strictLocalize({
                id: "voBUNT",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Sitemap"
                    }
                ]
            })
        },
        includeImprint ? {
            link: imprintPagePath(),
            text: strictLocalize({
                id: "lp+8HF",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Imprint"
                    }
                ]
            })
        } : null
    ].filter(isNotNull);
};
